import { forwardRef, useId, type InputHTMLAttributes } from "react";
import { cn } from "../../utils/cn";
import { Field, fieldStateClass } from "./Field";

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helper?: string;
  error?: string;
  leftIcon?: string;
  rightIcon?: string;
  onRightIconClick?: () => void;
  onLeftIconClick?: () => void;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    label,
    helper,
    error,
    leftIcon,
    rightIcon,
    onRightIconClick,
    onLeftIconClick,
    className,
    id,
    required,
    ...rest
  },
  ref
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <Field label={label} htmlFor={inputId} helper={helper} error={error} required={required}>
      <div className="relative">
        {leftIcon ? (
          <button
            type="button"
            onClick={onLeftIconClick}
            tabIndex={-1}
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-y-0 left-0 flex w-9 items-center justify-center text-foreground-400",
              onLeftIconClick && "pointer-events-auto cursor-pointer"
            )}
          >
            <i className={`${leftIcon} text-base`} />
          </button>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          className={cn(fieldStateClass(error, className), leftIcon && "pl-9", rightIcon && "pr-9", "h-9")}
          aria-invalid={Boolean(error)}
          {...rest}
        />
        {rightIcon ? (
          <button
            type="button"
            onClick={onRightIconClick}
            aria-label="Toggle field action"
            className="absolute inset-y-0 right-0 flex w-9 cursor-pointer items-center justify-center text-foreground-400 hover:text-foreground-700"
          >
            <i className={`${rightIcon} text-base`} />
          </button>
        ) : null}
      </div>
    </Field>
  );
});